import { ComponentProps, ReactNode, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Alert from '@amzn/meridian/alert';
import Column from '@amzn/meridian/column';
import Heading from '@amzn/meridian/heading';
import Icon from '@amzn/meridian/icon';
import Link from '@amzn/meridian/link';
import Text from '@amzn/meridian/text';
import { useTheme as useMeridianTheme } from '@amzn/meridian/theme';
import searchIconTokens from '@amzn/meridian-tokens/base/icon/search';
import plusCircleIconTokens from '@amzn/meridian-tokens/base/icon/plus-circle';
import chatIconTokens from '@amzn/meridian-tokens/base/icon/chat';
import { css } from '@emotion/css';
import { withAlpha } from '../theme/color';
import meridianOrangePalette from '../theme/meridian-orange';
import { createTransition, fadeUpEntrance, motionDurations, reducedMotionQuery, staggerDelay } from '../theme/motion';
import ActionButton from '../components/ActionButton';
import HeroSearchBar from '../components/HeroSearchBar';
import viewFilledIconTokens from '@amzn/meridian-tokens/base/icon/view-filled';
import ServicePagination from '../components/ServicePagination';
import ServiceTable from '../components/ServiceTable';
import { ServiceCardSkeletonList, SkeletonLine } from '../components/Skeletons';
import { useServices } from '../hooks/useServices';
import useSurfaceStyles from '../hooks/useSurfaceStyles';

interface Capability {
  title: string;
  description: ReactNode;
  iconTokens: ComponentProps<typeof Icon>['tokens'];
}

const capabilities: Capability[] = [
  {
    title: 'Specs for each service',
    description: 'Search generated MCP specs by service name.',
    iconTokens: searchIconTokens,
  },
  {
    title: 'Two-click MCP creation',
    description: (
      <>
        View specs here, create your MCP in the{' '}
        <Link href="https://admin.iris.aft.amazon.dev/" target="_blank" rel="noopener noreferrer">
          IRIS AI Studio
        </Link>
        .
      </>
    ),
    iconTokens: plusCircleIconTokens,
  },
  {
    title: 'Discovery agent',
    description: 'Chat to understand services and their relationships.',
    iconTokens: chatIconTokens,
  },
];

// The API pages results without a total, so a full page with more to come reads as "20+".
function formatResultCount(resultCount: number, hasMoreResults: boolean): string {
  const countLabel = hasMoreResults ? `${resultCount}+` : `${resultCount}`;
  return `${countLabel} ${resultCount === 1 && !hasMoreResults ? 'service' : 'services'}`;
}

const entranceClass = css({
  animation: fadeUpEntrance,
  [reducedMotionQuery]: { animation: 'none' },
});

export default function ServiceSearch() {
  const navigate = useNavigate();
  const meridianTheme = useMeridianTheme();
  const { surfaceClass, accentIconClass } = useSurfaceStyles();
  const [searchParams, setSearchParams] = useSearchParams();
  const submittedTerm = searchParams.get('q')?.trim() || '';
  const [searchValue, setSearchValue] = useState(submittedTerm);
  const { services, isLoading, error, page, hasNextPage, hasPreviousPage, nextPage, previousPage, refresh } =
    useServices(submittedTerm || undefined, { enabled: Boolean(submittedTerm) });
  const isDarkMode = meridianTheme.mode === 'dark';
  const heroClass = css({
    position: 'relative',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 32,
    paddingTop: submittedTerm ? 8 : 72,
    paddingBottom: submittedTerm ? 8 : 24,
    transition: createTransition(['padding'], motionDurations.moderate),
    '&::before': {
      content: '""',
      position: 'absolute',
      inset: '-48px 0 auto 0',
      height: 360,
      pointerEvents: 'none',
      background: `radial-gradient(ellipse 60% 70% at 50% 0%, ${withAlpha(
        meridianOrangePalette.palette300,
        isDarkMode ? 0.16 : 0.12,
      )} 0%, transparent 70%)`,
      opacity: submittedTerm ? 0 : 1,
      transition: createTransition(['opacity'], motionDurations.moderate),
    },
    [reducedMotionQuery]: { transition: 'none', '&::before': { transition: 'none' } },
  });
  const heroContentClass = css({
    position: 'relative',
    width: '100%',
    maxWidth: 720,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 20,
  });
  const eyebrowClass = css({
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    color: meridianTheme('themeInkSubdued'),
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
  });
  const eyebrowMarkClass = css({
    display: 'flex',
    color: meridianTheme('themeActionPrimaryDefault'),
  });
  const heroTitleClass = css({
    margin: 0,
    fontSize: 40,
    lineHeight: 1.1,
    fontWeight: 650,
    letterSpacing: '-0.025em',
    textAlign: 'center',
    color: meridianTheme('themeInkDefault'),
    '@media (max-width: 720px)': { fontSize: 30 },
  });
  const resultCountClass = css({
    minHeight: 20,
    color: meridianTheme('themeInkSubdued'),
    fontSize: 13,
    fontWeight: 500,
    lineHeight: '20px',
    fontVariantNumeric: 'tabular-nums',
  });
  const capabilityGridClass = css({
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    width: '100%',
    maxWidth: 560,
  });
  const capabilityCardClass = css({
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    padding: '14px 18px',
  });

  useEffect(() => {
    setSearchValue(submittedTerm);
  }, [submittedTerm]);

  const handleSearchSubmit = () => {
    const nextTerm = searchValue.trim();

    if (nextTerm === submittedTerm) {
      refresh();
      return;
    }

    const nextSearchParams = new URLSearchParams(searchParams);
    if (nextTerm) {
      nextSearchParams.set('q', nextTerm);
    } else {
      nextSearchParams.delete('q');
    }
    setSearchParams(nextSearchParams);
  };

  const handleClear = () => {
    setSearchValue('');
    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.delete('q');
    setSearchParams(nextSearchParams, { replace: true });
  };

  return (
    <Column spacing="500" width="100%" minWidth={0}>
      <div className={heroClass}>
        <div className={heroContentClass}>
          {!submittedTerm && (
            <span className={`${eyebrowClass} ${entranceClass}`}>
              <span className={eyebrowMarkClass}>
                <Icon tokens={viewFilledIconTokens} />
              </span>
              IRIS Catalog
            </span>
          )}
          <h1 className={heroTitleClass}>Search MCP specs by service name</h1>
          {!submittedTerm && (
            <Text type="b300" color="secondary" alignment="center">
              Find generated tool definitions for any onboarded service, then build your MCP in minutes.
            </Text>
          )}
          <HeroSearchBar
            value={searchValue}
            onChange={setSearchValue}
            onSubmit={handleSearchSubmit}
            onClear={handleClear}
          />
        </div>

        {!submittedTerm && (
          <div className={capabilityGridClass}>
            {capabilities.map((capability, position) => (
              <div
                key={capability.title}
                className={`${surfaceClass} ${capabilityCardClass} ${entranceClass}`}
                style={{ animationDelay: staggerDelay(position + 1) }}
              >
                <span className={accentIconClass}>
                  <Icon tokens={capability.iconTokens} />
                </span>
                <Column spacing="100">
                  <Text type="h100">{capability.title}</Text>
                  <Text type="b200" color="secondary">
                    {capability.description}
                  </Text>
                </Column>
              </div>
            ))}
          </div>
        )}
      </div>

      {error && (
        <Alert type="error" size="large" title="Search failed" titleLevel={2}>
          {error}
        </Alert>
      )}

      {submittedTerm && (isLoading || services.length > 0) && (
        <Column spacing="300" width="100%" minWidth={0}>
          <div className={resultCountClass} aria-live="polite">
            {isLoading && services.length === 0 ? (
              <SkeletonLine width={88} lineHeight={20} />
            ) : (
              formatResultCount(services.length, hasNextPage)
            )}
          </div>
          {isLoading && services.length === 0 ? (
            <ServiceCardSkeletonList label="Searching services" />
          ) : (
            <ServiceTable services={services} onNavigate={navigate} />
          )}
          <ServicePagination
            page={page}
            hasNextPage={hasNextPage}
            hasPreviousPage={hasPreviousPage}
            isLoading={isLoading}
            onNextPage={nextPage}
            onPreviousPage={previousPage}
          />
        </Column>
      )}

      {submittedTerm && services.length === 0 && !isLoading && !error && (
        <Column alignmentHorizontal="center" spacingInset="600" spacing="300">
          <Heading level={2} type="h300">
            No services found
          </Heading>
          <Text type="b200" color="secondary">
            No services match "{submittedTerm}". Try a different service name.
          </Text>
          <ActionButton variant="secondary" onClick={handleClear}>
            Clear search
          </ActionButton>
        </Column>
      )}
    </Column>
  );
}
